import {
  AdviceProductDetailsBackendValues,
  DisabilityAdditionalData,
} from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { EntityTypeOld, INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import {
  AkaCriticalIllness,
  ChildCoverage,
  CriticalIllnessCoverage,
  DeathCoverage,
  DisabilityCoverage,
} from "Store/interfaces/products/coverage";

export type ADVICE_NAMES =
  | INSURANCE_NAME_VALUE.DISABILITY_FLEX
  | INSURANCE_NAME_VALUE.DEATH
  | INSURANCE_NAME_VALUE.CRITICAL_ILLNESS;

export interface AdviceResponse {
  readonly payload: Advice;
  readonly errors?: AdviceError[];
}

export interface AdviceError {
  readonly errorCode: string;
  readonly errorMessage?: string;
}

export interface Advice {
  readonly advice: Record<ADVICE_NAMES, InsuranceAdvice>;
  readonly authorizationLevel: UserAuthorizationLevel;
}

export enum UserAuthorizationLevel {
  CUSTOMER = "CUSTOMER",
  ADVISOR = "ADVISOR",
  AUTHORIZED_ADVISOR = "AUTHORIZED_ADVISOR",
  PRODUCT_MANAGER = "PRODUCT_MANAGER",
}

export interface InsuranceAdvice {
  readonly recommendedCoverage?: InsuranceCoverage;
  readonly singlePayment: SinglePayment;
  readonly yearlyPayment?: YearlyPayment;
}

export enum InsuranceCoverage {
  BASIC = "BASIC",
  PLUS = "PLUS",
  TOP = "TOP",
}

export interface SinglePayment {
  readonly recommendationConfiguration: PurchaseRecommendationConfiguration;
  readonly restrictions?: PaymentRestriction;
  readonly advice?: PaymentAdvice;
}

export interface YearlyPayment extends SinglePayment {
  readonly details?: YearlyPaymentDetails;
}

export interface PurchaseRecommendationConfiguration {
  readonly recommendation: PurchaseRecommendation;
  readonly causes?: RecommendationReason[];
}

export enum PurchaseRecommendation {
  PURCHASE = "PURCHASE",
  PURCHASE_FROM_ADVISOR = "PURCHASE_FROM_ADVISOR",
  CHECK_DISABILITY_PENSION_AND_PURCHASE = "CHECK_DISABILITY_PENSION_AND_PURCHASE",
  NOT_RECOMMENDED = "NOT_RECOMMENDED",
  NOT_ONLINE = "NOT_ONLINE",
  NOT_ALLOWED = "NOT_ALLOWED",
}

export enum RecommendationReason {
  AGE = "AGE",
  STUDENT = "STUDENT",
  WORK_SITUATION = "WORK_SITUATION",
  DISABILITY_PENSION = "DISABILITY_PENSION",
  DEPENDENTS = "DEPENDENTS",
  INCOME = "INCOME",
  PAYOUT_TOO_HIGH = "PAYOUT_TOO_HIGH",
  ECONOMICAL_HEALTH_WISH = "ECONOMICAL_HEALTH_WISH",
}

export interface PaymentRestriction {
  readonly minPayment: number;
  readonly maxPayouts: Record<UserAuthorizationLevel, number>;
}

export interface PaymentAdvice {
  readonly recommended: number;
  readonly minValid: number;
  readonly maxValid: number;
  readonly paymentReasons: Partial<Record<PaymentReason, number>>;
}

export enum PaymentReason {
  INCOME_DEPENDENTS = "INCOME_DEPENDENTS",
  DEBT_DEPENDENTS = "DEBT_DEPENDENTS",
  MINOR_CHILDREN = "MINOR_CHILDREN",
  INCOME = "INCOME",
  INCOME_GAP = "INCOME_GAP",
  ECONOMICAL_HEALTH_WISH = "ECONOMICAL_HEALTH_WISH",
}

export interface YearlyPaymentDetails {
  readonly netIncome: number;
  readonly eligibleForPaymentAfter3Months: boolean;
  readonly netNavPayout: number;
  readonly netEmployerPaidDisabilityPension: number;
  readonly yearlyPaymentTaxRate: number;
}

export interface AdviceState {
  adviceRequest: AdviceProductDetailsBackendValues | null;
  adviceResult: AdviceResponseEntity;
  advicePriceCalculationRequests: AdvicePriceCalculationRequest;
  advicePriceCalculationResults: AdvicePriceCalculationResponseEntity;
}

type AdviceResponseEntity = EntityTypeOld<Advice, OperationType>;

export interface AdvicePriceCalculationRequest {
  initialRequest: AdviceChanges;
  changedByCustomer: AdviceChanges;
}

export interface AdviceChanges {
  [insuranceName: string]: AdviceChange;
}

export interface AdviceChange {
  insuranceMonthlyPrice?: number;
  productCheckedToPurchase: boolean;
  sliderValue: number;
  priceCalculationRequest: {
    productPriceUrl: string;
    insuranceName: INSURANCE_NAME_VALUE;
    data: { [key: string]: any };
  };
  additionalData?: DisabilityAdditionalData;
}

type AdvicePriceCalculationResponseEntity = EntityTypeOld<
  AdvicePriceCalculationResult[],
  OperationType
>;

export enum OperationType {
  SEND_GET_ADVICE_REQUEST = "SEND_GET_ADVICE_REQUEST",
  SEND_ADVICE_PRICE_CALCULATION_REQUEST = "SEND_ADVICE_PRICE_CALCULATION_REQUEST",
  SEND_CUSTOMER_PRICE_CALCULATION_REQUEST = "SEND_CUSTOMER_PRICE_CALCULATION_REQUEST",
  UPDATE_ADVICE_IN_CART_REQUEST = "UPDATE_ADVICE_IN_CART_REQUEST",
}

export interface AdviceProductUpdate {
  adviceProductChangeType: AdviceProductChangeType;
  insuranceName: INSURANCE_NAME_VALUE;
  productCheckedToPurchase?: boolean;
  sliderValue: number;
  priceCalculationRequest: {
    productPriceUrl: string;
    insuranceName: INSURANCE_NAME_VALUE;
    data: { [key: string]: any };
  };
  additionalData?: DisabilityAdditionalData;
}

export enum AdviceProductChangeType {
  INITIAL_SETUP,
  PRODUCT_CHECKBOX,
  PRODUCT_SLIDER,
  ONE_TIME_PAYMENT_CHECKBOX,
  ADVISOR_ADVICED_ONE_TIME_PAYMENT,
  ADVISOR_CUSTOM_ONE_TIME_PAYMENT,
  ADVISOR_NO_ONE_TIME_PAYMENT,
  ADVISOR_PAYMENT_AFTER_3_MONTHS,
}

export const AdviceProductCustomerChangeTypes = [
  AdviceProductChangeType.PRODUCT_SLIDER,
  AdviceProductChangeType.ONE_TIME_PAYMENT_CHECKBOX,
  AdviceProductChangeType.ADVISOR_ADVICED_ONE_TIME_PAYMENT,
  AdviceProductChangeType.ADVISOR_CUSTOM_ONE_TIME_PAYMENT,
  AdviceProductChangeType.ADVISOR_NO_ONE_TIME_PAYMENT,
  AdviceProductChangeType.ADVISOR_PAYMENT_AFTER_3_MONTHS,
];

export interface AdvicePriceCalculationResult {
  configurationName: AdviceCoverageName;
  compositions: string[];
  prices: [
    {
      monthlyPrice: number;
      monthlyPricePrecise: number;
      yearlyPrice: number;
      yearlyPricePrecise: number;
      fullMonthlyPrice: number;
      fullMonthlyPricePrecise: number;
      fullYearlyPrice: number;
      fullYearlyPricePrecise: number;
    },
  ];
}

export type AdviceCoverageName =
  | AkaCriticalIllness
  | CriticalIllnessCoverage
  | DeathCoverage
  | DisabilityCoverage
  | ChildCoverage;
